import { type VideoLinkItem } from '@solink/vd-data-layer/api-clients/platform/platform';
import { type VMSCameraId } from '@solinkcorp/api-types';
import { type IEvent } from 'clients/PlatformApi';
import { type PlayEventHandler } from 'components/EventPlayer/types';
import { type NormalizedEventItem } from 'src/components/VideoPlayer/components/VideoHistogram/types';

export const VERIFEYE_OFFSET = 2000; // 2 seconds

export const isValidDetection = (item: VideoLinkItem | NormalizedEventItem): boolean => {
  if (item.title === 'Loitering maximum reached') return true;
  const { detectionTimestamp = 0, peopleDetected = 0, vehicleDetected = 0 } = item.metadata ?? {};
  return detectionTimestamp > 0 && (peopleDetected > 0 || vehicleDetected > 0);
};

export const getValidDetectionEvents = (
  videoLinkItems: VideoLinkItem[] | undefined,
): VideoLinkItem[] => videoLinkItems?.filter((item) => isValidDetection(item)) || [];

const toPositiveMotionTime = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return undefined;
};

/** Motion time for a detection link item. Matches alarm timeline item time resolution. */
export const getDetectionMotionTime = (
  item: VideoLinkItem | NormalizedEventItem,
): number | undefined => {
  const detectionTimestamp = toPositiveMotionTime(item.metadata?.detectionTimestamp);
  if (detectionTimestamp !== undefined) return detectionTimestamp;
  if ('timestamp' in item) {
    const timestamp = toPositiveMotionTime(item.timestamp);
    if (timestamp !== undefined) return timestamp;
  }
  if ('time' in item) {
    const time = toPositiveMotionTime(item.time);
    if (time !== undefined) return time;
  }
  return undefined;
};

export const getDetectionSeekTime = (
  item: VideoLinkItem | NormalizedEventItem,
): number | undefined => {
  const motionTime = getDetectionMotionTime(item);
  return motionTime !== undefined ? motionTime - VERIFEYE_OFFSET : undefined;
};

/** Pick the alarm timeline item to seek/switch to when a tick groups multiple cameras. */
export const getTargetAlarmEventItem = (
  eventItems: NormalizedEventItem[],
  playingVmsCameraId?: VMSCameraId,
): NormalizedEventItem | undefined => {
  if (playingVmsCameraId) {
    const matchingItem = eventItems.find((item) => item.cameraIds.includes(playingVmsCameraId));
    if (matchingItem) return matchingItem;
  }
  return eventItems[0];
};

/** Earliest valid detection, optionally scoped to one camera. Matches alarm timeline dedupe. */
export const getEarliestValidDetection = (
  videoLinkItems: VideoLinkItem[] | undefined,
  cameraId?: VMSCameraId,
): VideoLinkItem | undefined => {
  const validDetections = getValidDetectionEvents(videoLinkItems).filter(
    (item) => !cameraId || item.cameraId === cameraId,
  );

  return validDetections.reduce<VideoLinkItem | undefined>((earliest, item) => {
    const ts = getDetectionMotionTime(item);
    if (ts === undefined) return earliest;
    const earliestTs = earliest ? getDetectionMotionTime(earliest) : undefined;
    if (earliestTs === undefined || ts < earliestTs) return item;
    return earliest;
  }, undefined);
};

/**
 * Plays an alarm event by finding the appropriate video link item and calculating the start time.
 *
 * @param playEvent - The event handler function that will be called to play the event
 * @param event - The event object containing video link items to be played
 * @param cameraId - Optional camera ID to filter for a specific camera's video link item
 *
 * @remarks
 * If a camera ID is provided, the function uses that camera's earliest valid detection.
 * Otherwise, it uses the earliest valid detection across all cameras.
 * The start time is calculated from the detection timestamp minus a verification offset.
 */
export const playAlarmEvent = (
  playEvent: PlayEventHandler,
  event: IEvent,
  cameraId?: VMSCameraId,
): number | undefined => {
  const videoLinkItem = getEarliestValidDetection(event.videoLinkItems, cameraId);
  const videoStartTime = videoLinkItem ? getDetectionSeekTime(videoLinkItem) : undefined;
  playEvent(event, null, videoStartTime, false);
  return videoStartTime;
};
